/**
 * Unique behaviors: what a named item (Unforgotten or Empyreal) does that no
 * rolled item can. Behaviors are typed data (`types/behaviors.ts`), the same
 * vocabulary skill perks use; crucible-host runs both. See
 * docs/gameplay/item-rarities.md, "Unique Behaviors".
 *
 * Numbers are first-pass, to tune.
 */
import type { AbilityChange } from './types/ability-changes';
import type { NamedItemBehavior } from './types/behaviors';
import { resolveCombinedCombatModifiers } from './essence-statuses';

/**
 * Folds one ability change into another (perks and items alike):
 * multipliers multiply, bonuses and charges add, the later override wins,
 * and riders gather.
 */
export function resolveAbilityChanges(a: AbilityChange, b: AbilityChange): AbilityChange {
    const multiply = (left: number | undefined, right: number | undefined): number | undefined =>
        left === undefined && right === undefined ? undefined : (left ?? 1) * (right ?? 1);
    const add = (left: number | undefined, right: number | undefined): number | undefined =>
        left === undefined && right === undefined ? undefined : (left ?? 0) + (right ?? 0);
    const folded: AbilityChange = {
        costMultiplier: multiply(a.costMultiplier, b.costMultiplier),
        cooldownMultiplier: multiply(a.cooldownMultiplier, b.cooldownMultiplier),
        powerMultiplier: multiply(a.powerMultiplier, b.powerMultiplier),
        durationMultiplier: multiply(a.durationMultiplier, b.durationMultiplier),
        extraCharges: add(a.extraCharges, b.extraCharges),
        radiusBonusTiles: add(a.radiusBonusTiles, b.radiusBonusTiles),
        radiusMultiplier: multiply(a.radiusMultiplier, b.radiusMultiplier),
        healMultiplier: multiply(a.healMultiplier, b.healMultiplier),
        magnitudeBonus: add(a.magnitudeBonus, b.magnitudeBonus),
        allyDamageFraction: b.allyDamageFraction ?? a.allyDamageFraction,
        hitStrengths: b.hitStrengths ?? a.hitStrengths,
        hasNoDrawback: a.hasNoDrawback || b.hasNoDrawback ? true : undefined,
        riders: a.riders || b.riders ? [...(a.riders ?? []), ...(b.riders ?? [])] : undefined,
        statusModifiers: a.statusModifiers || b.statusModifiers ? resolveCombinedCombatModifiers([a.statusModifiers, b.statusModifiers]) : undefined,
        weaveCostFraction: b.weaveCostFraction ?? a.weaveCostFraction,
        traitStrengthMultiplier: multiply(a.traitStrengthMultiplier, b.traitStrengthMultiplier),
        extraWovenAbilities: add(a.extraWovenAbilities, b.extraWovenAbilities),
        repeatPower: b.repeatPower ?? a.repeatPower,
    };
    // Only what either side set: an empty change stays empty.
    return Object.fromEntries(Object.entries(folded).filter(([, value]) => value !== undefined)) as AbilityChange;
}

/** Whether a behavior is one only Empyreal items may carry. */
export function isEmpyrealOnlyBehavior(behavior: NamedItemBehavior): boolean {
    return behavior.kind === 'rule-override';
}
